/**
 * Weekly Report PDF Generator
 *
 * Generates a landscape "slide deck" PDF with:
 * - Cover page
 * - P&L all brands + per-brand pages
 * - Order flows per-brand pages
 * - Support summary page
 *
 * Uses jsPDF + jspdf-autotable for layout.
 */

import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

const PAGE_W = 792 // letter landscape width in pt
const PAGE_H = 612
const M = 40 // margin
const CONTENT_W = PAGE_W - M * 2

// Colors
const PRIMARY = [30, 64, 175] // blue-800
const MUTED = [100, 116, 139] // slate-500
const GREEN = [22, 163, 74]
const RED = [220, 38, 38]
const DARK = [15, 23, 42] // slate-900

function fmt$(cents) {
  if (cents == null || isNaN(cents)) return '-'
  const dollars = cents / 100
  if (Math.abs(dollars) >= 1000) {
    return '$' + (dollars / 1000).toFixed(1) + 'k'
  }
  return '$' + dollars.toFixed(0)
}

function fmtDollars(cents) {
  if (cents == null || isNaN(cents)) return '-'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(cents / 100)
}

function fmtPct(value) {
  if (value == null || isNaN(value)) return '-'
  return value.toFixed(1) + '%'
}

function fmtNum(value) {
  if (value == null || isNaN(value)) return '-'
  return new Intl.NumberFormat('en-US').format(value)
}

function fmtHours(hours) {
  if (hours == null || isNaN(hours)) return '-'
  if (hours < 1) return (hours * 60).toFixed(0) + 'm'
  return hours.toFixed(1) + 'h'
}

function fmtMinutes(mins) {
  if (mins == null || isNaN(mins)) return '-'
  if (mins < 60) return mins.toFixed(0) + 'm'
  return (mins / 60).toFixed(1) + 'h'
}

function delta(current, prior) {
  if (current == null || prior == null || prior === 0) return null
  return ((current - prior) / Math.abs(prior)) * 100
}

function deltaStr(current, prior) {
  const d = delta(current, prior)
  if (d == null) return '-'
  const sign = d >= 0 ? '+' : ''
  return sign + d.toFixed(1) + '%'
}

function pageTitle(doc, title, subtitle) {
  doc.setTextColor(...PRIMARY)
  doc.setFontSize(22)
  doc.setFont('helvetica', 'bold')
  doc.text(title, M, M + 20)
  if (subtitle) {
    doc.setTextColor(...MUTED)
    doc.setFontSize(11)
    doc.setFont('helvetica', 'normal')
    doc.text(subtitle, M, M + 38)
  }
  doc.setTextColor(...DARK)
  return M + 55
}

function addFooter(doc, weekLabel) {
  const pageCount = doc.internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    doc.setFontSize(8)
    doc.setTextColor(...MUTED)
    doc.setFont('helvetica', 'normal')
    doc.text(weekLabel, M, PAGE_H - 20)
    doc.text(`Page ${i} of ${pageCount}`, PAGE_W - M, PAGE_H - 20, { align: 'right' })
  }
}

// ── Cover Page ────────────────────────────────────────────────

function addCoverPage(doc, weekStart, weekEnd) {
  doc.setFillColor(30, 64, 175)
  doc.rect(0, 0, PAGE_W, PAGE_H, 'F')

  doc.setTextColor(255, 255, 255)
  doc.setFontSize(40)
  doc.setFont('helvetica', 'bold')
  doc.text('Weekly Report', PAGE_W / 2, PAGE_H / 2 - 40, { align: 'center' })

  doc.setFontSize(20)
  doc.setFont('helvetica', 'normal')
  doc.text(`${weekStart} — ${weekEnd}`, PAGE_W / 2, PAGE_H / 2 + 10, { align: 'center' })

  doc.setFontSize(12)
  doc.setTextColor(200, 210, 255)
  doc.text(`Generated ${new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}`, PAGE_W / 2, PAGE_H / 2 + 45, { align: 'center' })
}

// ── P&L Page ──────────────────────────────────────────────────

function addPnlPage(doc, title, weekData, t30Data) {
  const weekSummary = weekData?.summary || {}
  const weekExpenses = weekData?.expenses || {}
  const t30Summary = t30Data?.summary || {}
  const t30Expenses = t30Data?.expenses || {}

  // Normalize trailing 30 to weekly average (divide by ~4.3 weeks)
  const t30Days = 30
  const weekDays = 7
  const ratio = weekDays / t30Days

  const t30Norm = {
    total_revenue: (t30Summary.total_revenue || 0) * ratio,
    total_expenses: (t30Summary.total_expenses || 0) * ratio,
    gross_profit: (t30Summary.gross_profit || 0) * ratio,
    total_orders: (t30Summary.total_orders || 0) * ratio,
    new_orders: (t30Summary.new_orders || 0) * ratio,
    refill_orders: (t30Summary.refill_orders || 0) * ratio,
    cogs: (t30Expenses.cogs || 0) * ratio,
    shipping: (t30Expenses.shipping || 0) * ratio,
    ad_spend: (t30Expenses.ad_spend || 0) * ratio,
    other: (t30Expenses.other || 0) * ratio,
  }

  let y = pageTitle(doc, `P&L — ${title}`, 'Week vs Trailing 30-Day Weekly Average')

  // Summary KPIs
  const kpis = [
    ['Revenue', fmtDollars(weekSummary.total_revenue), fmtDollars(t30Norm.total_revenue), deltaStr(weekSummary.total_revenue, t30Norm.total_revenue)],
    ['Expenses', fmtDollars(weekSummary.total_expenses), fmtDollars(t30Norm.total_expenses), deltaStr(weekSummary.total_expenses, t30Norm.total_expenses)],
    ['Gross Profit', fmtDollars(weekSummary.gross_profit), fmtDollars(t30Norm.gross_profit), deltaStr(weekSummary.gross_profit, t30Norm.gross_profit)],
    ['Margin %', fmtPct(weekSummary.margin_percent), fmtPct(t30Summary.margin_percent), deltaStr(weekSummary.margin_percent, t30Summary.margin_percent)],
    ['', '', '', ''],
    ['Total Orders', fmtNum(weekSummary.total_orders), fmtNum(Math.round(t30Norm.total_orders)), deltaStr(weekSummary.total_orders, t30Norm.total_orders)],
    ['New Orders', fmtNum(weekSummary.new_orders), fmtNum(Math.round(t30Norm.new_orders)), deltaStr(weekSummary.new_orders, t30Norm.new_orders)],
    ['Refill Orders', fmtNum(weekSummary.refill_orders), fmtNum(Math.round(t30Norm.refill_orders)), deltaStr(weekSummary.refill_orders, t30Norm.refill_orders)],
    ['', '', '', ''],
    ['COGS', fmtDollars(weekExpenses.cogs), fmtDollars(t30Norm.cogs), deltaStr(weekExpenses.cogs, t30Norm.cogs)],
    ['Shipping', fmtDollars(weekExpenses.shipping), fmtDollars(t30Norm.shipping), deltaStr(weekExpenses.shipping, t30Norm.shipping)],
    ['Ad Spend', fmtDollars(weekExpenses.ad_spend), fmtDollars(t30Norm.ad_spend), deltaStr(weekExpenses.ad_spend, t30Norm.ad_spend)],
    ['Other', fmtDollars(weekExpenses.other), fmtDollars(t30Norm.other), deltaStr(weekExpenses.other, t30Norm.other)],
  ]

  autoTable(doc, {
    startY: y,
    head: [['Metric', 'This Week', 'T30 Avg/Wk', 'Delta']],
    body: kpis,
    theme: 'grid',
    styles: { fontSize: 11, cellPadding: 6, font: 'helvetica' },
    headStyles: { fillColor: PRIMARY, textColor: [255, 255, 255], fontStyle: 'bold' },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 140 },
      1: { halign: 'right', cellWidth: 130 },
      2: { halign: 'right', cellWidth: 130 },
      3: { halign: 'right', cellWidth: 100 },
    },
    margin: { left: M, right: M },
    didParseCell: (data) => {
      // Color delta column
      if (data.column.index === 3 && data.section === 'body') {
        const val = data.cell.raw
        if (typeof val === 'string') {
          if (val.startsWith('+')) data.cell.styles.textColor = GREEN
          else if (val.startsWith('-')) data.cell.styles.textColor = RED
        }
      }
      // Gray out empty separator rows
      if (data.section === 'body' && data.row.raw[0] === '') {
        data.cell.styles.minCellHeight = 4
        data.cell.styles.cellPadding = 1
      }
    },
  })
}

// ── P&L All Brands with Brand Breakdown ───────────────────────

function addPnlAllBrandsPage(doc, weekAgg, t30Agg) {
  addPnlPage(doc, 'All Brands', weekAgg, t30Agg)

  // Brand breakdown mini-table on same page if space allows
  const brandWeek = weekAgg?.brand_breakdown || []
  const brandT30 = t30Agg?.brand_breakdown || []
  const t30Map = {}
  for (const b of brandT30) t30Map[b.clinicId] = b

  if (brandWeek.length > 0) {
    const y = (doc.lastAutoTable?.finalY || 300) + 20
    const ratio = 7 / 30

    const rows = brandWeek.map(b => {
      const t30 = t30Map[b.clinicId]
      const t30Rev = t30 ? (t30.summary?.total_revenue || 0) * ratio : null
      const t30Prof = t30 ? (t30.summary?.gross_profit || 0) * ratio : null
      const t30Ord = t30 ? (t30.summary?.total_orders || 0) * ratio : null
      return [
        b.displayName,
        fmtDollars(b.summary?.total_revenue),
        fmtDollars(b.summary?.gross_profit),
        fmtPct(b.summary?.margin_percent),
        fmtNum(b.summary?.total_orders),
        deltaStr(b.summary?.total_revenue, t30Rev),
        deltaStr(b.summary?.total_orders, t30Ord),
      ]
    })

    autoTable(doc, {
      startY: y,
      head: [['Brand', 'Revenue', 'Profit', 'Margin', 'Orders', 'Rev \u0394', 'Ord \u0394']],
      body: rows,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 4, font: 'helvetica' },
      headStyles: { fillColor: [71, 85, 105], textColor: [255, 255, 255], fontStyle: 'bold' },
      columnStyles: {
        0: { fontStyle: 'bold' },
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'right' },
        6: { halign: 'right' },
      },
      margin: { left: M, right: M },
      didParseCell: (data) => {
        if (data.column.index >= 5 && data.section === 'body') {
          const val = data.cell.raw
          if (typeof val === 'string') {
            if (val.startsWith('+')) data.cell.styles.textColor = GREEN
            else if (val.startsWith('-')) data.cell.styles.textColor = RED
          }
        }
      },
    })
  }
}

// ── Order Flows Page ──────────────────────────────────────────

function addOrderFlowsPage(doc, brandName, flowData) {
  let y = pageTitle(doc, `Order Flows — ${brandName}`, 'Step durations: This Week vs Trailing 30 (completed orders)')

  const steps = flowData?.steps || {}
  const rows = []

  const stepOrder = ['Time_at_Doctor', 'Time_at_Pharmacy', 'Time_in_Shipping', 'Time_to_Route', 'Total_Order_Time']

  for (const key of stepOrder) {
    const step = steps[key]
    if (!step) continue

    const primary = step.primary?.completed || {}
    const comp = step.comparison?.completed || {}

    rows.push([
      step.label || key,
      fmtHours(primary.median),
      fmtHours(primary.p90),
      fmtNum(primary.count),
      fmtHours(comp.median),
      fmtHours(comp.p90),
      fmtNum(comp.count),
    ])
  }

  if (rows.length === 0) {
    doc.setFontSize(12)
    doc.setTextColor(...MUTED)
    doc.text('No order flow data available for this period.', M, y + 20)
    return
  }

  autoTable(doc, {
    startY: y,
    head: [['Step', 'Wk Median', 'Wk P90', 'Wk Count', 'T30 Median', 'T30 P90', 'T30 Count']],
    body: rows,
    theme: 'grid',
    styles: { fontSize: 11, cellPadding: 6, font: 'helvetica' },
    headStyles: { fillColor: PRIMARY, textColor: [255, 255, 255], fontStyle: 'bold' },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 160 },
      1: { halign: 'right' },
      2: { halign: 'right' },
      3: { halign: 'right' },
      4: { halign: 'right' },
      5: { halign: 'right' },
      6: { halign: 'right' },
    },
    margin: { left: M, right: M },
  })

  // Pending orders section
  const pendingY = (doc.lastAutoTable?.finalY || y) + 20
  const pendingRows = []

  for (const key of stepOrder) {
    const step = steps[key]
    if (!step) continue
    const pending = step.primary?.pending || {}
    if (pending.count > 0) {
      pendingRows.push([
        step.label || key,
        fmtNum(pending.count),
        fmtHours(pending.median),
        fmtHours(pending.p90),
      ])
    }
  }

  if (pendingRows.length > 0) {
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...DARK)
    doc.text('Currently Pending', M, pendingY)

    autoTable(doc, {
      startY: pendingY + 10,
      head: [['Step', 'Pending', 'Median Wait', 'P90 Wait']],
      body: pendingRows,
      theme: 'grid',
      styles: { fontSize: 10, cellPadding: 5, font: 'helvetica' },
      headStyles: { fillColor: [180, 83, 9], textColor: [255, 255, 255], fontStyle: 'bold' },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 160 },
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'right' },
      },
      margin: { left: M, right: M },
    })
  }
}

// ── Support Page ──────────────────────────────────────────────

function addSupportPage(doc, weekData, t30Data) {
  let y = pageTitle(doc, 'Support Overview — All Brands', 'Zendesk metrics: This Week vs Trailing 30-Day Weekly Average')

  const weekSummary = weekData?.summary || {}
  const t30Summary = t30Data?.summary || {}
  const ratio = 7 / 30

  const t30Norm = {
    tickets_created: (t30Summary.tickets_created || 0) * ratio,
    tickets_solved: (t30Summary.tickets_solved || 0) * ratio,
    avg_first_reply_minutes: t30Summary.avg_first_reply_minutes,
    avg_solve_time_hours: t30Summary.avg_solve_time_hours,
  }

  const summaryRows = [
    ['Tickets Created', fmtNum(weekSummary.tickets_created), fmtNum(Math.round(t30Norm.tickets_created)), deltaStr(weekSummary.tickets_created, t30Norm.tickets_created)],
    ['Tickets Solved', fmtNum(weekSummary.tickets_solved), fmtNum(Math.round(t30Norm.tickets_solved)), deltaStr(weekSummary.tickets_solved, t30Norm.tickets_solved)],
    ['CSAT Score', weekSummary.csat_score != null ? fmtPct(weekSummary.csat_score * 100) : '-', t30Summary.csat_score != null ? fmtPct(t30Summary.csat_score * 100) : '-', '-'],
    ['Avg First Reply', fmtMinutes(weekSummary.avg_first_reply_minutes), fmtMinutes(t30Summary.avg_first_reply_minutes), deltaStr(weekSummary.avg_first_reply_minutes, t30Summary.avg_first_reply_minutes)],
    ['Avg Solve Time', fmtHours(weekSummary.avg_solve_time_hours), fmtHours(t30Summary.avg_solve_time_hours), deltaStr(weekSummary.avg_solve_time_hours, t30Summary.avg_solve_time_hours)],
  ]

  autoTable(doc, {
    startY: y,
    head: [['Metric', 'This Week', 'T30 Avg/Wk', 'Delta']],
    body: summaryRows,
    theme: 'grid',
    styles: { fontSize: 11, cellPadding: 6, font: 'helvetica' },
    headStyles: { fillColor: PRIMARY, textColor: [255, 255, 255], fontStyle: 'bold' },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 180 },
      1: { halign: 'right', cellWidth: 130 },
      2: { halign: 'right', cellWidth: 130 },
      3: { halign: 'right', cellWidth: 100 },
    },
    margin: { left: M, right: M },
    didParseCell: (data) => {
      if (data.column.index === 3 && data.section === 'body') {
        const val = data.cell.raw
        if (typeof val === 'string') {
          // For support metrics, lower is better for times, higher is better for CSAT
          if (val.startsWith('+')) data.cell.styles.textColor = RED // more tickets/time = bad
          else if (val.startsWith('-')) data.cell.styles.textColor = GREEN
        }
      }
    },
  })

  // Per-brand breakdown
  const weekBrands = weekData?.brand_breakdown || []
  const t30Brands = t30Data?.brand_breakdown || []
  const t30Map = {}
  for (const b of t30Brands) t30Map[b.clinicId] = b

  if (weekBrands.length > 0) {
    const brandY = (doc.lastAutoTable?.finalY || y) + 25

    doc.setFontSize(13)
    doc.setFont('helvetica', 'bold')
    doc.setTextColor(...DARK)
    doc.text('Per-Brand Breakdown (This Week)', M, brandY)

    const brandRows = weekBrands.map(b => [
      b.displayName,
      fmtNum(b.tickets_created),
      fmtNum(b.tickets_solved),
      b.csat_score != null ? fmtPct(b.csat_score * 100) : '-',
      fmtMinutes(b.avg_first_reply_minutes),
      fmtHours(b.avg_solve_time_hours),
    ])

    autoTable(doc, {
      startY: brandY + 12,
      head: [['Brand', 'Created', 'Solved', 'CSAT', 'Avg Reply', 'Avg Solve']],
      body: brandRows,
      theme: 'grid',
      styles: { fontSize: 10, cellPadding: 5, font: 'helvetica' },
      headStyles: { fillColor: [71, 85, 105], textColor: [255, 255, 255], fontStyle: 'bold' },
      columnStyles: {
        0: { fontStyle: 'bold' },
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right' },
        5: { halign: 'right' },
      },
      margin: { left: M, right: M },
    })
  }
}

// ── Main Export ───────────────────────────────────────────────

export function generateWeeklyReportPdf({
  weekStart,
  weekEnd,
  pnlWeek,
  pnlT30,
  orderFlows, // { clinicId: { displayName, data } }
  supportWeek,
  supportT30,
}) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter' })

  const weekLabel = `${weekStart} — ${weekEnd}`

  // 1. Cover
  addCoverPage(doc, weekStart, weekEnd)

  // 2. P&L All Brands
  doc.addPage()
  addPnlAllBrandsPage(doc, pnlWeek, pnlT30)

  // 3. P&L per brand
  const weekBrands = pnlWeek?.brand_breakdown || []
  const t30Brands = pnlT30?.brand_breakdown || []
  const t30BrandMap = {}
  for (const b of t30Brands) t30BrandMap[b.clinicId] = b

  for (const brand of weekBrands) {
    doc.addPage()
    const t30Brand = t30BrandMap[brand.clinicId]
    addPnlPage(doc, brand.displayName, brand, t30Brand)
  }

  // 4. Order flows per brand
  const brandOrder = weekBrands.map(b => b.clinicId)
  for (const clinicId of brandOrder) {
    const flow = orderFlows[clinicId]
    if (!flow) continue
    doc.addPage()
    addOrderFlowsPage(doc, flow.displayName, flow.data)
  }

  // 5. Support
  doc.addPage()
  addSupportPage(doc, supportWeek, supportT30)

  // Add footers to all pages
  addFooter(doc, weekLabel)

  return doc
}
